// Editar tarefa (US-005) e diálogo de confirmação (excluir pede confirmação — ASM-003).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LIMITS, validateTaskInput, type Task, type TaskError, type TaskField } from '../domain/task';
import { useStore } from '../state/AppStore';
import { Icon } from './Icon';
import { TaskDetailsEditor, toInput, type FieldsState } from './NewTaskForm';

export function Dialog({ labelId, children, onClose, className }: { labelId: string; children: ReactNode; onClose(): void; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={className ? `dialog ${className}` : 'dialog'} role="dialog" aria-modal="true" aria-labelledby={labelId} ref={ref}>
        {children}
      </div>
    </div>
  );
}

interface ConfirmProps {
  title: string;
  body: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm(): void | Promise<void>;
  onCancel(): void;
}

export function ConfirmDialog({ title, body, confirmLabel, danger, onConfirm, onCancel }: ConfirmProps) {
  const { t } = useTranslation();
  return (
    <Dialog labelId="confirm-title" onClose={onCancel}>
      <h2 id="confirm-title" className="dialog__title">
        {title}
      </h2>
      <p className="dialog__body">{body}</p>
      <div className="dialog__actions">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="button" className={danger ? 'btn btn--danger' : 'btn btn--primary'} onClick={() => void onConfirm()}>
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

export function EditTaskDialog({ task, onClose }: { task: Task; onClose(): void }) {
  const { t } = useTranslation();
  const store = useStore();
  const [fields, setFields] = useState<FieldsState>({
    title: task.title,
    description: task.description ?? '',
    date: task.due_date,
    time: task.due_time ?? '',
    estimate: task.estimate_minutes,
  });
  const [errors, setErrors] = useState<Partial<Record<TaskField, TaskError>>>({});

  const save = async () => {
    const v = validateTaskInput(toInput(fields));
    if (!v.ok) {
      setErrors(v.errors);
      return;
    }
    onClose();
    await store.updateTask(task.id, v.value);
  };

  return (
    <Dialog labelId="edit-title" onClose={onClose}>
      <div className="dialog__head">
        <h2 id="edit-title" className="dialog__title">
          {t('task.editTitle')}
        </h2>
        <button type="button" className="icon-btn" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <input
          className="input input--title"
          aria-label={t('task.titleField')}
          value={fields.title}
          onChange={(e) => setFields({ ...fields, title: e.target.value })}
        />
        {errors.title && <p className="field-error">{t(`errors.task.${errors.title}`, { max: LIMITS.title })}</p>}
        <TaskDetailsEditor value={fields} onChange={setFields} errors={errors} />
        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn--primary">
            <Icon name="check" size={14} />
            {t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
